import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigConstants, FormParticipantTitles, isDefined, Mutable, ParticipantTitle } from '@evorait/pace-core';
import { faCaretDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { saveParticipantParticulars } from '../../../store/actions/form.actions';
import {
    getCurrentlyEditedParticipant,
    getFormParticipantCopyModal,
    getFormParticipantTitles,
} from '../../../store/selectors/form.selector';
import { FormsState } from '../../../store/state/pace-forms.state';
import { ParticipantCopy, ParticipantParticulars } from '../../../types/participant-copy';
import { ParticipantCopyModal } from '../../../types/participant-copy-modal';

@Component({
    selector: 'pace-add-participant',
    templateUrl: './add-participant.component.html',
    styleUrls: ['./add-participant.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddParticipantComponent implements OnInit, OnDestroy {
    public participantCopyModal$: Observable<ParticipantCopyModal>;
    public participantCopy$: Observable<ParticipantCopy>;
    public participantTitles$: Observable<FormParticipantTitles>;

    public localParticipant: Mutable<ParticipantParticulars>;

    public showTitlesDropdownMenu = false;

    public participantForm: FormGroup;

    public notReceivingCredControl: FormControl;
    public titleControl: FormControl;
    public firstNameControl: FormControl;
    public lastNameControl: FormControl;
    public mailControl: FormControl;

    private participantCopySubscription: Subscription;

    private notReceivingCredSubscription: Subscription;
    private titleControlSubscription: Subscription;
    private firstNameControlSubscription: Subscription;
    private lastNameControlSubscription: Subscription;
    private mailControlSubscription: Subscription;

    public faCheck = faCheck;
    public faCaretDown = faCaretDown;
    private emailPattern = '^([a-zA-Z0-9\\+_\\-]+)(\\.[a-zA-Z0-9\\+_\\-]+)*@([a-zA-Z0-9\\-]+\\.)+[a-zA-Z]{2,6}$';
    public constructor(
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
        private readonly store: Store<FormsState>,
    ) {
        this.participantTitles$ = store.select(getFormParticipantTitles);
        this.participantCopyModal$ = store.select(getFormParticipantCopyModal);
        this.participantCopy$ = store.select(getCurrentlyEditedParticipant).pipe(filter(isDefined));
    }

    public ngOnInit(): void {
        this.notReceivingCredControl = this.formBuilder.control(false);
        /* eslint-disable @typescript-eslint/unbound-method */
        this.titleControl = this.formBuilder.control('');
        this.firstNameControl = this.formBuilder.control('', Validators.required);
        this.lastNameControl = this.formBuilder.control('', Validators.required);
        this.mailControl = this.formBuilder.control('', [Validators.required, Validators.pattern(this.emailPattern)]);
        /* eslint-enable @typescript-eslint/unbound-method */

        this.participantForm = this.formBuilder.group({
            notReceivingCredit: this.notReceivingCredControl,
            title: this.titleControl,
            firstName: this.firstNameControl,
            lastName: this.lastNameControl,
            mail: this.mailControl,
        });

        // Not receiving credit checkbox is only valid for PACE US
        this.notReceivingCredSubscription = this.notReceivingCredControl.valueChanges.subscribe((newVal: boolean) => {
            this.localParticipant.notReceivingCredit = newVal;
            if (newVal) {
                // In Pace US if the not-receiving credit is checked, then the e-mail is not mandatory
                this.mailControl.clearValidators();
            } else {
                // eslint-disable-next-line @typescript-eslint/unbound-method
                this.mailControl.setValidators([Validators.required, Validators.pattern(this.emailPattern)]);
            }
            this.mailControl.updateValueAndValidity();
        });

        this.titleControlSubscription = this.titleControl.valueChanges
            .pipe(debounceTime(ConfigConstants.debounceTimeMs), distinctUntilChanged())
            .subscribe((newVal: string) => (this.localParticipant.title = newVal));

        this.firstNameControlSubscription = this.firstNameControl.valueChanges
            .pipe(debounceTime(ConfigConstants.debounceTimeMs), distinctUntilChanged())
            .subscribe((newVal: string) => (this.localParticipant.firstName = newVal));

        this.lastNameControlSubscription = this.lastNameControl.valueChanges
            .pipe(debounceTime(ConfigConstants.debounceTimeMs), distinctUntilChanged())
            .subscribe((newVal: string) => (this.localParticipant.lastName = newVal));

        this.mailControlSubscription = this.mailControl.valueChanges
            .pipe(debounceTime(ConfigConstants.debounceTimeMs), distinctUntilChanged())
            .subscribe((newVal: string) => (this.localParticipant.email = newVal));

        this.participantCopySubscription = this.participantCopy$.subscribe((participant) => {
            this.localParticipant = {
                email: participant.email,
                firstName: participant.firstName,
                id: participant.id,
                lastName: participant.lastName,
                notReceivingCredit: participant.notReceivingCredit,
                selectedTitle: participant.selectedTitle,
                selectedInParticipantCopiesModal: participant.selectedInParticipantCopiesModal,
                title: participant.title,
            };

            this.notReceivingCredControl.patchValue(this.localParticipant.notReceivingCredit);
            this.titleControl.patchValue(this.localParticipant.title);
            this.firstNameControl.patchValue(this.localParticipant.firstName);
            this.lastNameControl.patchValue(this.localParticipant.lastName);
            this.mailControl.patchValue(this.localParticipant.email);
        });
    }

    public save(): void {
        this.store.dispatch(
            saveParticipantParticulars({
                participant: this.localParticipant,
            }),
        );

        this.modalService.setDismissReason('Participant saved');
        this.modalInstance.hide();
    }

    public toggleTitlesDropdown(): void {
        this.showTitlesDropdownMenu = !this.showTitlesDropdownMenu;
    }

    public onTitleSelected(entry: ParticipantTitle): void {
        this.toggleTitlesDropdown();
        if (typeof this.localParticipant === 'undefined') {
            throw new Error(`AddParticipantComponent: onTitleSelected: Local copy of participant is undefined!`);
        }
        this.localParticipant.selectedTitle = entry.value;
        if (this.isOtherTitleSelected(entry.value)) {
            this.localParticipant.title = '';
            // eslint-disable-next-line @typescript-eslint/unbound-method
            this.titleControl.setValidators([Validators.required]);
        } else {
            this.localParticipant.title = entry.value;
            this.titleControl.clearValidators();
        }
        this.titleControl.patchValue(this.localParticipant.title);

        this.titleControl.updateValueAndValidity();
    }

    public ngOnDestroy(): void {
        if (typeof this.participantCopySubscription !== 'undefined') {
            this.participantCopySubscription.unsubscribe();
        }

        if (typeof this.notReceivingCredSubscription !== 'undefined') {
            this.notReceivingCredSubscription.unsubscribe();
        }

        if (typeof this.titleControlSubscription !== 'undefined') {
            this.titleControlSubscription.unsubscribe();
        }

        if (typeof this.firstNameControlSubscription !== 'undefined') {
            this.firstNameControlSubscription.unsubscribe();
        }

        if (typeof this.lastNameControlSubscription !== 'undefined') {
            this.lastNameControlSubscription.unsubscribe();
        }

        if (typeof this.mailControlSubscription !== 'undefined') {
            this.mailControlSubscription.unsubscribe();
        }
    }

    public isOtherTitleSelected(title: string): boolean {
        return title === ConfigConstants.attendeeTitleOther;
    }
}
